import { useCallback, useRef, useState } from 'react';
import useAnalytics from './useAnalytics';
import { useIdleTimer } from 'react-idle-timer';

export enum CHAT_SESSION_END_REASON {
  INACTIVITY = 'inactivity',
  APP_CLOSE = 'app_close',
  ROUTE_CHANGE = 'route_change',
}

export const useAnalyticsSession = (startEventName: string, endEventName: string) => {
  const [isActive, setIsActive] = useState(false);
  const sessionStartTime = useRef<number | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const analytics = useAnalytics();

  // Start a new session
  const startSession = useCallback(() => {
    const id = crypto.randomUUID();
    sessionIdRef.current = id;
    setIsActive(true);
    sessionStartTime.current = Date.now();
    analytics.track(startEventName, { sessionId: id });
  }, [analytics, startEventName]);

  // End the current session
  const endSession = (reason: CHAT_SESSION_END_REASON) => {
    if (analytics && sessionIdRef.current && sessionStartTime.current) {
      analytics.track(
        endEventName,
        {
          sessionId: sessionIdRef.current,
          duration: Date.now() - sessionStartTime.current,
          reason,
        },
        { send_immediately: true }
      );
    }
    setIsActive(false);
    sessionIdRef.current = null;
    sessionStartTime.current = null;
  };

  // Idle detection
  useIdleTimer({
    timeout: 120_000, // 2 minutes
    onIdle: () => endSession(CHAT_SESSION_END_REASON.INACTIVITY),
    onActive: () => {
      if (!isActive) startSession();
    },
    debounce: 500,
  });

  return { startSession, endSession };
};
